import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { FieldValidatorFn } from '@ngx-formly/core/lib/models';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldTypeWithControl,
  ISPValidationErrors,
  ISPValidator,
} from '../model';
import { getOriginalControlFromConfig } from '../utils/formly-configs-generation';

/**
 * Get file max size validator function
 *
 * @param control
 * @param config
 */
function fileMaxSizeValidator(
  control: AbstractControl,
  config: ISPFieldConfig<ISPFieldType.File>,
): ISPValidationErrors | null {
  const value = control.value;
  if (!(value instanceof FileList)) {
    return null;
  }

  const originalControl = config.props.originalControl;
  const maxSize = originalControl.$maxsize
    ? Number(originalControl.$maxsize)
    : NaN;
  const totalSize = Array.from(value).reduce(
    (size, file) => size + file.size,
    0,
  );

  if (totalSize > maxSize) {
    return {
      [ISPValidator.FileMaxSize]: true,
    };
  }

  return null;
}

/**
 * Checks duplicates in the control's value
 *
 * @param control - abstract control
 */
function duplicateValidator(
  control: AbstractControl,
): ISPValidationErrors | null {
  const value = control.value;
  if (typeof value !== 'string') {
    return null;
  }

  const splitedValue = control.value.split(/\s+/);
  const uniqueValues = new Set<string>(splitedValue);
  if (uniqueValues.size !== splitedValue.length) {
    return {
      [ISPValidator.Duplicate]: true,
    };
  }

  return null;
}

/**
 * Match values of two fields
 *
 * @WARN must be used only on group fields!
 * @param passwordFormGroup - password Form Group with 'password' and 'confirm' controls
 * @param config
 */
function passMatchValidator(
  passwordFormGroup: UntypedFormGroup,
  config: ISPFieldConfig<ISPFieldType.Group>,
): ISPValidationErrors | null {
  const passwordConfig = config.fieldGroup.find(
    c =>
      c.type === ISPFieldType.Password && c.props.originalControl.$checkpasswd,
  ) as ISPFieldConfig<ISPFieldType.Password> | undefined;
  if (!passwordConfig) {
    return null;
  }

  const passwordControl = passwordFormGroup.controls[
    passwordConfig.key
  ] as UntypedFormControl;
  if (!passwordControl) {
    return null;
  }

  const confirmConfigKey: string | undefined =
    passwordConfig.props.originalControl.$checkpasswd;
  if (!confirmConfigKey) {
    return null;
  }

  const confirmConfig = config.fieldGroup.find(
    c => c.type === ISPFieldType.Password && c.key === confirmConfigKey,
  ) as ISPFieldConfig<ISPFieldType.Password> | undefined;
  const confirmControl = passwordFormGroup.controls[
    confirmConfigKey
  ] as UntypedFormControl;
  if (!confirmConfig?.options?.detectChanges || !confirmControl) {
    return null;
  }

  const password: string = passwordControl.value;
  const confirm: string = confirmControl.value;

  // avoid displaying the message error when values are empty
  if (!password) {
    return null;
  }

  if (confirm === password) {
    confirmControl.setErrors(null);
    // in order to trigger 'invalid' flag changing in password component
    confirmConfig.options.detectChanges(confirmConfig);
    return null;
  }

  confirmControl.markAsTouched();
  confirmControl.setErrors({
    [ISPValidator.PassMatch]: true,
  });
  // in order to trigger 'invalid' flag changing in password component
  confirmConfig.options.detectChanges(confirmConfig);

  return {
    [ISPValidator.PassMatch]: true,
  };
}

/**
 * Error after form submit validator function. Need to display error on the field
 *
 * @param formControl - form control
 * @param fieldConfig - field config
 * @returns - true for valid value, false for invalid
 */
function submitErrorValidator(
  formControl: AbstractControl,
  fieldConfig: ISPFieldConfig<ISPFieldTypeWithControl>,
): ISPValidationErrors | null {
  if (fieldConfig.type === ISPFieldType.Captcha) {
    return Boolean(formControl.value)
      ? null
      : {
          [ISPValidator.ErrorFromBackend]: true,
        };
  }

  if (formControl.parent?.pristine) {
    return {
      [ISPValidator.ErrorFromBackend]: true,
    };
  }
  return null;
}

/**
 * Check that field is not empty
 *
 * @param formControl - form control
 * @param fieldConfig - field config
 * @returns - true for valid value, false for invalid
 */
function requiredValidator(
  formControl: AbstractControl,
  fieldConfig: ISPFieldConfig<ISPFieldTypeWithControl>,
): ISPValidationErrors | null {
  const control = getOriginalControlFromConfig(fieldConfig);
  // ignore mixed fields requires
  if (control?.$mixed === 'yes') {
    return null;
  }

  if (fieldConfig.type === 'checkbox' && formControl.value === 'off') {
    return {
      [ISPValidator.Required]: true,
    };
  }

  if (
    fieldConfig.type === 'select' &&
    !fieldConfig.props.multiple &&
    formControl.value === 'null'
  ) {
    return {
      [ISPValidator.Required]: true,
    };
  }

  return Validators.required(formControl);
}

export const ISP_VALIDATORS: Record<ISPValidator, FieldValidatorFn> = {
  [ISPValidator.FileMaxSize]: fileMaxSizeValidator,
  [ISPValidator.Duplicate]: duplicateValidator,
  [ISPValidator.PassMatch]: passMatchValidator,
  [ISPValidator.ErrorFromBackend]: submitErrorValidator,
  // eslint-disable-next-line @typescript-eslint/unbound-method
  [ISPValidator.Required]: requiredValidator,
};
